import React, { useCallback } from "react";
import PropTypes from "prop-types";
import {
  NodeRepeatableList,
  NodeRadioGroup,
  NodeSelect,
  NodeTextField,
} from "../NodeUI";

/**
 * Repeatable editor for the outbound headers sent to an MCP server. Models any static-auth scheme:
 *  - "bearer": Authorization header with a "Bearer " prefix and a secret token
 *  - "secret": a named header whose value is a secret (Credential Store)
 *  - "text":   a named header with a literal, non-sensitive value
 *
 * No headers means the server is called unauthenticated.
 */

const KIND_OPTIONS = [
  { value: "bearer", label: "Bearer token" },
  { value: "secret", label: "Secret header" },
  { value: "text", label: "Plain text" },
];

let rowSeq = 0;
export function createHeaderRow(kind = "bearer") {
  rowSeq += 1;
  const base = {
    id: `mcp_h_${Date.now()}_${rowSeq}`,
    kind,
    name: "",
    valuePrefix: "",
    secret: kind !== "text",
    mode: "new",
    value: "",
    credentialId: "",
    secretValue: "",
  };
  if (kind === "bearer") {
    base.name = "Authorization";
    base.valuePrefix = "Bearer ";
  }
  return base;
}

/**
 * Maps a stored or template header "view" (name/valuePrefix/secret, no secret
 * value) to an editable form row, inferring the auth kind. Shared by the server
 * dialog (rehydrating an existing connection) and template application so the
 * kind-inference rule lives in exactly one place.
 */
export function headerViewToRow(view) {
  const secret = Boolean(view?.secret);
  let kind = "text";
  if (secret) {
    kind =
      view?.valuePrefix === "Bearer " && view?.name === "Authorization"
        ? "bearer"
        : "secret";
  }
  return {
    ...createHeaderRow(kind),
    name: view?.name || "",
    valuePrefix: view?.valuePrefix || "",
    secret,
    mode: view?.credentialId ? "existing" : "new",
    value: secret ? "" : view?.value || "",
    credentialId: view?.credentialId || "",
    secretValue: "",
  };
}

function applyKind(item, kind) {
  const next = { ...item, kind };
  if (kind === "bearer") {
    next.name = "Authorization";
    next.valuePrefix = "Bearer ";
    next.secret = true;
  } else {
    next.valuePrefix = "";
    next.secret = kind === "secret";
    if (item.name === "Authorization") next.name = "";
  }
  return next;
}

const McpHeadersField = ({ headers, onChange, credentials, disabled }) => {
  const hasExisting = Array.isArray(credentials) && credentials.length > 0;

  const renderRow = useCallback(
    (item, index, { updateItem }) => {
      const set = (patch) => updateItem(index, { ...item, ...patch });

      return (
        <>
          <NodeSelect
            label="Type"
            value={item.kind}
            onChange={(kind) => updateItem(index, applyKind(item, kind))}
            disabled={disabled}
            options={KIND_OPTIONS}
          />

          <NodeTextField
            label="Header name"
            value={item.name}
            onChange={(value) => set({ name: value })}
            placeholder={item.kind === "text" ? "X-Org-Id" : "X-Api-Key"}
            disabled={disabled || item.kind === "bearer"}
          />

          {!item.secret ? (
            <NodeTextField
              label="Value"
              value={item.value}
              onChange={(value) => set({ value })}
              placeholder="Header value"
              disabled={disabled}
            />
          ) : (
            <>
              <NodeRadioGroup
                label="Secret source"
                value={item.mode}
                onChange={(mode) => set({ mode })}
                row
                disabled={disabled}
                options={[
                  { value: "existing", label: "Use existing", disabled: !hasExisting },
                  { value: "new", label: "Create new" },
                ]}
              />
              {item.mode === "existing" ? (
                <NodeSelect
                  label="Stored credential"
                  value={item.credentialId || ""}
                  onChange={(credentialId) => set({ credentialId })}
                  disabled={disabled || !hasExisting}
                  placeholder={
                    hasExisting ? "Select a credential" : "No saved secrets yet"
                  }
                  options={(credentials || []).map((credential) => ({
                    value: credential.credentialId,
                    label:
                      credential.name ||
                      credential.placeholder ||
                      credential.credentialId,
                  }))}
                />
              ) : (
                <NodeTextField
                  label={item.kind === "bearer" ? "Token" : "Secret value"}
                  type="password"
                  value={item.secretValue || ""}
                  onChange={(secretValue) => set({ secretValue })}
                  placeholder="Token / API key"
                  disabled={disabled}
                  helperText="Saved to the Credential Store when you save this server."
                  inputProps={{ autoComplete: "new-password" }}
                />
              )}
            </>
          )}
        </>
      );
    },
    [credentials, hasExisting, disabled]
  );

  return (
    <NodeRepeatableList
      label="Headers"
      items={headers}
      onItemsChange={onChange}
      createItem={() => createHeaderRow("bearer")}
      renderItem={renderRow}
      addLabel="Add header"
      emptyMessage="No headers — the server is called without authentication."
      disabled={disabled}
    />
  );
};

McpHeadersField.propTypes = {
  headers: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  credentials: PropTypes.array,
  disabled: PropTypes.bool,
};

export default McpHeadersField;
